import { useContext, useState } from 'react';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import EDDialog from '../../../../../BaseComponents/Dialog/EDDialog';
import EDCheckbox from '../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { EDTextEdit } from '../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDDatePicker from '../../../../../BaseComponents/DatePicker/EDDatePicker';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import {
  EDAutoCompleteEdit,
  ISuggestion,
} from '../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { TEDGAHandelseKLAREnum } from '../../../../../globalQueryTypes';
import { LoginStateContext } from '../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import EDAvenyProgress from '../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';

export interface IHandelseFormValues {
  rubrik: string;
  text: string;
  bevakning: string;
  ansvarigSuggestion: ISuggestion | null;
  klar: TEDGAHandelseKLAREnum | null;
  signatur: string;
}

interface IHandelseDialogProps {
  open: boolean;
  isNew: boolean;
  editingItem: any;
  rubrikOptions: ISuggestion[];
  ansvarigOptions: ISuggestion[];
  paperClassName: string;
  loading?: boolean;
  onClose: () => void;
  onSave: (values: IHandelseFormValues) => void;
}

const getInitialFormValues = (
  isNew: boolean,
  editingItem: any,
  rubrikOptions: ISuggestion[],
  ansvarigOptions: ISuggestion[],
  sign: string
) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowString = tomorrow.toISOString().split('T')[0];

  if (isNew) {
    return {
      rubrikSuggestion: null as ISuggestion | null,
      rubrikInput: '',
      text: '',
      bevakning: tomorrowString,
      ansvarigSuggestion: null as ISuggestion | null,
      klar: null as TEDGAHandelseKLAREnum | null,
      signatur: sign,
      mottagare: '',
    };
  }
  const matchingRubrik = rubrikOptions.find(
    opt => opt.name === editingItem?.RUBRIK
  );
  return {
    rubrikSuggestion: matchingRubrik ?? {
      value: editingItem?.RUBRIK,
      name: editingItem?.RUBRIK,
      data: null,
    },
    rubrikInput: editingItem?.RUBRIK ?? '',
    text: editingItem?.TEXT ?? '',
    bevakning: editingItem?.BEVAKNINGSDATUM ?? '',
    ansvarigSuggestion:
      ansvarigOptions.find(
        opt => opt.name === editingItem?._Associations?.Ansvarig?.ANSVARIG_NAMN
      ) ?? null,
    klar:
      editingItem?.KLAR === TEDGAHandelseKLAREnum.KLAR
        ? TEDGAHandelseKLAREnum.KLAR
        : TEDGAHandelseKLAREnum.NOTERING,
    signatur: editingItem?.SIGNATUR ?? '',
    mottagare: editingItem?.MOTTAGARE ?? '',
  };
};

export const HandelseDialog: React.FC<IHandelseDialogProps> = ({
  open,
  isNew,
  editingItem,
  rubrikOptions,
  ansvarigOptions,
  paperClassName,
  loading = false,
  onClose,
  onSave,
}) => {
  const loginStateContext = useContext(LoginStateContext);

  const initial = getInitialFormValues(
    isNew,
    editingItem,
    rubrikOptions,
    ansvarigOptions,
    loginStateContext.sign ?? ''
  );

  const [rubrikSuggestion, setRubrikSuggestion] = useState<ISuggestion | null>(
    initial.rubrikSuggestion
  );
  const [rubrikInput, setRubrikInput] = useState(initial.rubrikInput);
  const [text, setText] = useState(initial.text);
  const [bevakning, setBevakning] = useState(initial.bevakning);
  const [ansvarigSuggestion, setAnsvarigSuggestion] =
    useState<ISuggestion | null>(initial.ansvarigSuggestion);
  const [klar, setKlar] = useState<TEDGAHandelseKLAREnum | null>(initial.klar);
  const [signatur] = useState(initial.signatur);

  const handleSave = () => {
    onSave({
      rubrik: rubrikSuggestion?.name || rubrikInput || '',
      text,
      bevakning,
      ansvarigSuggestion,
      klar,
      signatur,
    });
  };

  return (
    <EDDialog
      open={open}
      onClose={onClose}
      title={isNew ? 'Ny händelse' : 'Redigera händelse'}
      subtitle={isNew ? undefined : editingItem?.RUBRIK}
      maxWidth="md"
      fullWidth
      classes={{ paper: paperClassName }}
      footerActions={[
        {
          label: 'Avbryt',
          onClick: onClose,
          buttonProps: {
            disabled: loading,
          },
        },
        {
          label: 'Spara',
          onClick: handleSave,
          buttonProps: {
            disabled: loading || (!rubrikSuggestion?.name && !rubrikInput),
          },
        },
      ]}
    >
      <EDBox
        sx={{ position: 'relative' }}
        display="flex"
        flexDirection="column"
        gap={2}
      >
        {loading && <EDAvenyProgress overlay />}

        <ObjektVyKort caption="Händelsedetaljer">
          {!isNew && (
            <EDBox mb={2}>
              <EDTypography variant="body2" color="textSecondary">
                Skapad: {editingItem?.DATUM || '-'}
              </EDTypography>
            </EDBox>
          )}

          <EDAutoCompleteEdit
            label="Rubrik"
            value={rubrikSuggestion}
            options={rubrikOptions}
            onChange={(suggestion: ISuggestion | null) => {
              setRubrikSuggestion(suggestion);
            }}
            onInputChange={(_event, value, reason) => {
              setRubrikInput(value);
              if (reason === 'input') {
                if (!value) {
                  setRubrikSuggestion(null);
                  return;
                }
                const matchingRubrik = rubrikOptions.find(
                  (opt: ISuggestion) => opt.name === value
                );
                setRubrikSuggestion(
                  matchingRubrik ?? { value, name: value, data: null }
                );
              }
            }}
            fullWidth
            freeSolo
            required
          />

          <EDDatePicker
            label="Bevakningsdatum"
            value={bevakning}
            onChange={value => setBevakning(value ?? '')}
            allowOnlyNumbers
            fullWidth
          />

          <EDAutoCompleteEdit
            label="Ansvarig"
            value={ansvarigSuggestion}
            options={ansvarigOptions}
            onChange={(suggestion: ISuggestion | null) => {
              setAnsvarigSuggestion(suggestion);
            }}
            fullWidth
            disabled={ansvarigOptions.length === 0}
          />

          <EDTextEdit
            label="Text"
            value={text}
            onChange={value => setText(value)}
            multiline
            minRows={4}
            fullWidth
          />

          <EDTextEdit label="Signatur" value={signatur} disabled fullWidth />

          <EDBox mt={2}>
            <EDCheckbox
              label="Markera som klar"
              checked={klar === TEDGAHandelseKLAREnum.KLAR}
              onCheck={isChecked =>
                setKlar(
                  isChecked
                    ? TEDGAHandelseKLAREnum.KLAR
                    : TEDGAHandelseKLAREnum.NOTERING
                )
              }
            />
          </EDBox>
        </ObjektVyKort>
      </EDBox>
    </EDDialog>
  );
};
